import React, { useState, useEffect, useRef } from 'react';
import { PageHeader, Button, Typography, Space, Row, Col } from 'antd';
import { MyBreadcrumb } from 'utils';
import { PrinterOutlined } from '@ant-design/icons';
import {
  handleApiError,
  axiosBase,
  renderInfoBlock,
  shouldShowBlock,
} from 'utils';
import { useReactToPrint } from 'react-to-print';

const { Title } = Typography;

function buildAddress(a1, a2, city, state, zip, country) {
  const lines = [];
  if (a1) lines.push(a1);
  if (a2) lines.push(a2);
  const cityLine = [city, state, zip].filter(Boolean).join(', ');
  if (cityLine) lines.push(cityLine);
  // Only include country if there are other address parts
  if (country && lines.length > 0) lines.push(country);
  return lines.join('\n');
}

export default function View(props) {
  const { id } = props.match.params;
  const axios = axiosBase.create();
  const [isLoading, setIsLoading] = useState(false);
  const [item, setItem] = useState({});
  const componentRef = useRef(null);

  useEffect(() => {
    console.log('props', props);
    if (id) {
      axios({
        method: 'GET',
        url: '/service-provider/view/' + id,
      })
        .then((response) => {
          setIsLoading(false);
          if (response.data.item) {
            setItem(response.data.item);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          handleApiError(error);
        });
    }
  }, [axios, id, props]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const noticesAddress = buildAddress(
    item.notices_address1, item.notices_address2,
    item.notices_city, item.notices_state, item.notices_zip, item.notices_country
  );

  const billingAddressFormatted = buildAddress(
    item.billing_address1, item.billing_address2,
    item.billing_city, item.billing_state, item.billing_zip, item.billing_country
  );

  const showReferral =
    item.referral_partner &&
    item.referral_partner.toString().toUpperCase() !== 'NO';

  const generalInfoItems = [
    'account_number',
    { key: 'Vendor ID', value: item.ap_vendor_id },
    { key: 'Service Code', value: item.ap_service_code },
    'status',
    'auto_pay',
    'payment_timeline',
    'where_invoice_sent',
    { key: 'AP Manager', value: item.ap_manager ? item.ap_manager.display_name : '' },
    'circuit_id_meter_id',
    ...(showReferral
      ? [
          'referral_partner',
          {
            key: 'Referred By',
            value: item.referred_by ? item.referred_by.service_provider : '',
          },
        ]
      : []),
  ];

  const noticesAddressItems = noticesAddress
    ? [{ key: 'address', value: noticesAddress, hideLabel: true }]
    : [];

  const contractInfoItems = [
    { key: 'Monthly Fee', value: item.monthly_fee ? `$${item.monthly_fee}` : '' },
    { key: 'Hourly Rate', value: item.hourly_rate ? `$${item.hourly_rate}` : '' },
    { key: 'Hourly Minimum', value: item.hourly_minimum ? `$${item.hourly_minimum}` : '' },
    'monthly_service_detail',
    'service_type',
    'contract_start',
    'contract_end',
    { key: 'Installation Fee', value: item.installation_fee ? `$${item.installation_fee}` : '' },
    { key: 'Signed By', value: item.contract_signedby },
    { key: 'SLA', value: item.sla },
  ];

  const billingAddressItems = [
    'billing_name',
    ...(billingAddressFormatted
      ? [{ key: 'address', value: billingAddressFormatted, hideLabel: true }]
      : []),
    { key: 'Contact', value: item.billing_contact },
    { key: 'Phone', value: item.billing_phone },
    { key: 'Email', value: item.billing_email },
    { key: 'Fax', value: item.billing_fax },
  ];

  const serviceProviderInfoItems = [
    { key: 'Provider', value: item.service_provider },
    { key: 'Sales Email', value: item.sales_agent_email },
    { key: 'Sales Phone', value: item.sales_agent_phone },
    { key: 'Sales Agent', value: item.sales_agent_name },
    'notes',
  ];

  return (
    <div>
      <MyBreadcrumb
        items={[
          { label: 'Service Providers', path: '/service-providers' },
          { label: 'View', path: '' },
        ]}
      />
      <PageHeader
        ghost={false}
        onBack={() => window.history.back()}
        title="View"
        subTitle=""
        extra={[
          <Button
            key="1"
            onClick={handlePrint}
            type="primary"
            shape="round"
            icon={<PrinterOutlined />}
          >
            Print
          </Button>,
        ]}
      >
        {isLoading ? 'Loading...' : ''}

        <div ref={componentRef}>
          <div className="descriptionView2-heading">
            <Title level={3}>
              Service Providers - {item?.service_provider}
            </Title>
            <Title level={3}>
              Status:{' '}
              {item.status === 'ACTIVE' ? (
                <span className="text-green">Active</span>
              ) : (
                <span className="text-yellow">{item.status}</span>
              )}
            </Title>
            {item.monthly_fee && (
              <Title level={3}>
                Monthly Fee:{' '}
                <span className="text-green">${item.monthly_fee}</span>
              </Title>
            )}
          </div>

          <div style={{ maxWidth: 900, margin: 'auto' }}>
            <Row gutter={[16, 16]}>
              {shouldShowBlock({
                heading: 'General Info',
                source: item,
                items: generalInfoItems,
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'General Info',
                    source: item,
                    items: generalInfoItems,
                  })}
                </Col>
              )}

              {noticesAddressItems.length > 0 && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Notices Address',
                    source: item,
                    items: noticesAddressItems,
                  })}
                </Col>
              )}

              {shouldShowBlock({
                heading: 'Contract Information',
                source: item,
                items: contractInfoItems,
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Contract Information',
                    source: item,
                    items: contractInfoItems,
                  })}
                </Col>
              )}

              {shouldShowBlock({
                heading: 'Credentials',
                source: item,
                items: [
                  'primary_on_account',
                  'pin',
                  'security_question',
                  'phone_number',
                  'username',
                  'password',
                  'website',
                  'email',
                ],
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Credentials',
                    source: item,
                    items: [
                      'primary_on_account',
                      'pin',
                      'security_question',
                      'phone_number',
                      'username',
                      'password',
                      'website',
                      'email',
                    ],
                  })}
                </Col>
              )}

              {shouldShowBlock({
                heading: 'Location',
                source: item,
                items: ['location_name', 'node_id'],
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Location',
                    source: item,
                    items: ['location_name', 'node_id'],
                  })}
                </Col>
              )}

              {shouldShowBlock({
                heading: 'Billing Address',
                source: item,
                items: billingAddressItems,
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Billing Address',
                    source: item,
                    items: billingAddressItems,
                  })}
                </Col>
              )}

              {shouldShowBlock({
                heading: 'Site Information',
                source: item,
                items: ['service_equipment_location'],
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Site Information',
                    source: item,
                    items: ['service_equipment_location'],
                  })}
                </Col>
              )}

              {shouldShowBlock({
                heading: 'Service Provider Information',
                source: item,
                items: serviceProviderInfoItems,
              }) && (
                <Col xs={24} xl={12}>
                  {renderInfoBlock({
                    heading: 'Service Provider Information',
                    source: item,
                    items: serviceProviderInfoItems,
                  })}
                </Col>
              )}
            </Row>
          </div>
        </div>
      </PageHeader>
    </div>
  );
}
